const GAP = 8;
const EDGE = 20;

export function overlapArea(a, b, gap = GAP) {
  return Math.max(0, Math.min(a.right, b.right + gap) - Math.max(a.left, b.left - gap)) *
    Math.max(0, Math.min(a.bottom, b.bottom + gap) - Math.max(a.top, b.top - gap));
}

export function chooseChatOffset(origin, viewport, obstacles, previous = {x: 0, y: 0}) {
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const minY = Math.max(EDGE, viewport.headerBottom + GAP);
  const maxY = Math.max(minY, viewport.height - EDGE - origin.height);
  const minX = EDGE, maxX = Math.max(minX, viewport.width - EDGE - origin.width);
  const rect = (x, y) => ({left:x, top:y, right:x + origin.width, bottom:y + origin.height});
  const score = r => obstacles.reduce((n, o) => n + overlapArea(r, o), 0);
  const fits = r => r.left >= minX && r.right <= viewport.width - EDGE + 1 && r.top >= minY && r.bottom <= viewport.height - EDGE + 1;
  const original = rect(origin.left, origin.top);
  if (fits(original) && score(original) === 0) return {x:0, y:0};
  const current = rect(origin.left + previous.x, origin.top + previous.y);
  if (fits(current) && score(current) === 0) return previous;
  const ys = new Set([clamp(origin.top,minY,maxY), minY, maxY]);
  for (const o of obstacles) for (const y of [o.top - GAP - origin.height, o.bottom + GAP]) ys.add(clamp(y,minY,maxY));
  const xs = [...new Set([clamp(origin.left,minX,maxX), minX, maxX])];
  let best;
  for (const left of xs) for (const top of ys) {
    const candidate = {x:left-origin.left, y:top-origin.top};
    const area = score(rect(left,top)), distance = Math.abs(candidate.x) + Math.abs(candidate.y);
    if (!best || area < best.area || (area === best.area && distance < best.distance)) best = {...candidate, area, distance};
  }
  return {x:best.x, y:best.y};
}

function installChatClearance() {
  let button, originalTranslate = '', originalPriority = '', offset = {x:0,y:0}, scheduled = false;
  const observed = new WeakSet();
  const observer = new MutationObserver(schedule);

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {scheduled = false; update();});
  }

  function watch(root) {
    if (observed.has(root)) return;
    observed.add(root);
    observer.observe(root, {childList:true, subtree:true});
  }

  function findLauncher() {
    if (button?.isConnected) return button;
    const host = document.querySelector('shopify-chat');
    if (!host) return null;
    const roots = [host];
    for (const root of roots) {
      watch(root);
      const candidate = [...root.querySelectorAll('button[aria-expanded]')].find(b => b.classList.contains('activator') || /chat|message/i.test(b.getAttribute('aria-label') || ''));
      if (candidate) {
        button = candidate;
        originalTranslate = button.style.getPropertyValue('translate');
        originalPriority = button.style.getPropertyPriority('translate');
        offset = {x:0,y:0};
        observer.observe(button, {attributes:true, attributeFilter:['aria-expanded']});
        return button;
      }
      if (root.shadowRoot) roots.push(root.shadowRoot);
      for (const e of root.querySelectorAll('*')) if (e.shadowRoot) roots.push(e.shadowRoot);
    }
    return null;
  }

  function visibleRect(e) {
    const r = e.getBoundingClientRect();
    if (!r.width || !r.height || r.bottom <= 0 || r.top >= innerHeight) return null;
    const style = getComputedStyle(e);
    return style.visibility === 'hidden' || style.display === 'none' ? null : r;
  }

  function protectedRects() {
    const rects = [];
    const homepage = document.querySelector('main [data-dq-image-banner]') && document.querySelector('main [data-dq-focused-setup]');
    for (const root of document.querySelectorAll('main, footer')) {
      for (const control of root.querySelectorAll('a,button,input,select,textarea,summary')) {
        // Card text stays protected below; reserving entire photos crowds out small links on mobile.
        if (homepage && control.matches('a.dqs-card, a.dqs-path')) continue;
        const r = visibleRect(control);
        if (r) rects.push(r);
      }
      // Text ranges protect prices, labels and notes without reserving whole image areas.
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!node.nodeValue.trim() || node.parentElement.closest('script,style,noscript,svg,[aria-hidden="true"]') || !visibleRect(node.parentElement)) continue;
        const range = document.createRange();
        range.selectNodeContents(node);
        for (const r of range.getClientRects()) if (r.width && r.height && r.bottom > 0 && r.top < innerHeight) rects.push(r);
      }
    }
    return rects;
  }

  function restore() {
    if (!offset.x && !offset.y) return;
    if (originalTranslate) button.style.setProperty('translate',originalTranslate,originalPriority);
    else button.style.removeProperty('translate');
    offset = {x:0,y:0};
  }

  function update() {
    const launcher = findLauncher();
    if (!launcher) return;
    // Expanded conversations retain the vendor's position, focus and close behavior.
    if (launcher.getAttribute('aria-expanded') === 'true') {restore(); return;}
    const r = visibleRect(launcher);
    if (!r) return;
    const origin = {left:r.left-offset.x, top:r.top-offset.y, width:r.width, height:r.height};
    const headerBottom = Math.max(0, document.querySelector('header')?.getBoundingClientRect().bottom || 0);
    const next = chooseChatOffset(origin,{width:document.documentElement.clientWidth,height:innerHeight,headerBottom},protectedRects(),offset);
    if (next.x === offset.x && next.y === offset.y) return;
    if (!next.x && !next.y) {restore(); return;}
    launcher.style.setProperty('translate',`${next.x}px ${next.y}px`,'important');
    offset = next;
  }

  watch(document.body);
  addEventListener('scroll',schedule,{passive:true,capture:true});
  addEventListener('resize',schedule,{passive:true});
  addEventListener('pageshow',schedule);
  document.addEventListener('load',schedule,true);
  document.fonts?.ready.then(schedule);
  customElements.whenDefined('shopify-chat').then(schedule);
  customElements.whenDefined('shopify-agent-runtime').then(schedule);
  schedule();
}

if (typeof document !== 'undefined') installChatClearance();
